import type {MyUser, User} from '@deezer/next/contexts/main/types';
import {StakeholderType} from '@deezer/next/contexts/main/types';
import {StakeholderType as StakeholderTypeAsString} from '@deezer/next/services/api/Starmania/requests/album/types';
import {FeatureLabel} from '@deezer/next/services/types/features';
import {NO_RIGHTS_STAKEHOLDER_ID} from '@constants/user';
import {ClaimAccessType} from '@deezer/next/features/ClaimAccess/types';
import type {Claim} from '@deezer/next/services/api/Starmania/requests/claim/types';
import {ClaimStatus} from '@deezer/next/services/api/Starmania/requests/claim/types';
import type {TFunction} from 'next-i18next';
import {Routes} from '@constants/routes';

export const isArtist = (user?: MyUser) =>
	user?.stakeholderType === StakeholderType.ARTIST;

export const isArtistManager = (user?: MyUser) =>
	user?.stakeholderType === StakeholderType.ARTIST_MANAGER;

export const isLabelGroup = (user?: MyUser) =>
	user?.stakeholderType === StakeholderType.LABEL_GROUP;

export const isArtistMarketing = (user?: MyUser) =>
	user?.stakeholderType === StakeholderType.ARTIST_MARKETING;

export const isArtistRelated = (user?: MyUser) =>
	isArtistManager(user) || isArtist(user);

export const isProvider = (user?: MyUser) =>
	user?.stakeholderType === StakeholderType.PROVIDER;

export const isUserApproved = (user?: MyUser) =>
	user?.stakeholderId !== NO_RIGHTS_STAKEHOLDER_ID;

export const hasFeature = (
	user: MyUser | User | undefined,
	feature: FeatureLabel,
): boolean => {
	if (!user || !user.features) return false;
	return user?.features.some(f => f.label === feature && f.enabled);
};

export const hasSearchRights = (user?: MyUser) => {
	if (!user?.features) return false;
	return user?.features.some(
		f =>
			(f.label === FeatureLabel.ARTISTS_EDITOR ||
				f.label === FeatureLabel.ANALYTICS_VIEWER) &&
			f.enabled,
	);
};

export const isAdmin = (user: MyUser | User | undefined) =>
	hasFeature(user, FeatureLabel.USERS_EDITOR);

export const hasPitchesRights = (user?: MyUser) => {
	if (!user?.features) return false;
	return user.features.some(
		feature => feature.label === FeatureLabel.PITCHING_TOOL && feature.enabled,
	);
};

export const isSomeClaimsPending = (claims: Claim[]) => {
	return (
		claims &&
		claims.length > 0 &&
		claims.some(
			claim =>
				claim.status === ClaimStatus.PENDING ||
				claim.status === ClaimStatus.PROCESSING ||
				claim.status === ClaimStatus.IN_PROGRESS,
		)
	);
};

export const isSomeClaimsPendingOrApproved = (claims: Claim[]) => {
	return (
		claims &&
		claims.length > 0 &&
		claims.some(
			claim =>
				claim.status === ClaimStatus.APPROVED ||
				claim.status === ClaimStatus.PENDING ||
				claim.status === ClaimStatus.PROCESSING ||
				claim.status === ClaimStatus.IN_PROGRESS,
		)
	);
};

export const hasApprovedClaim = (claims: Claim[]) => {
	return claims?.some(c => c.status === ClaimStatus.APPROVED) || false;
};

export const getUserAnalyticsRoute = (user?: MyUser) => {
	if (!user) return Routes.HOME;

	if (isArtistMarketing(user)) {
		return Routes.ANALYTIC_ARTIST_MARKETING;
	}

	if (isProvider(user)) {
		return Routes.ANALYTIC_PROVIDER_VIEW;
	}

	if (isLabelGroup(user)) {
		return Routes.ANALYTIC_LABEL_VIEW;
	}

	return Routes.ANALYTIC_ARTIST_VIEW;
};

export const getUserDefaultRoute = (user?: MyUser, activeArtist?: number) => {
	if (!user) return Routes.HOME;
	const hasAnalyticAccess =
		user.uriRedirectionOnLogin === 'analytic/view' ||
		user.uriRedirectionOnLogin === '/analytic/view';
	const userRoute = hasAnalyticAccess
		? getUserAnalyticsRoute(user)
		: user.uriRedirectionOnLogin;
	return isArtistManager(user) && !activeArtist ? Routes.PROFILES : userRoute;
};

export const getClaimAccessTypeAsString = (
	claimTypeNumber: StakeholderType,
): ClaimAccessType | undefined => {
	switch (claimTypeNumber) {
		case StakeholderType.ARTIST:
			return ClaimAccessType.ARTIST;
		case StakeholderType.ARTIST_MANAGER:
			return ClaimAccessType.MANAGER;
		case StakeholderType.LABEL_GROUP:
			return ClaimAccessType.LABEL;
		case StakeholderType.PROVIDER:
			return ClaimAccessType.PROVIDER;
		default:
			break;
	}
};

export const getStakeholderTypeAsString = (
	stakeholderTypeNumber?: StakeholderType,
): StakeholderTypeAsString | undefined => {
	switch (stakeholderTypeNumber) {
		case StakeholderType.ARTIST:
			return StakeholderTypeAsString.ARTIST;
		case StakeholderType.ARTIST_MANAGER:
			return StakeholderTypeAsString.MANAGER;
		case StakeholderType.LABEL_GROUP:
			return StakeholderTypeAsString.LABEL;
		case StakeholderType.PROVIDER:
			return StakeholderTypeAsString.PROVIDER;
		default:
			break;
	}
};

export const getTranslatedFeature = (feature: FeatureLabel, t: TFunction) => {
	switch (feature) {
		case FeatureLabel.ANALYTICS_VIEWER:
			return t('usermanager_text_analyticsviewer_d4c-web');
		case FeatureLabel.ARTISTS_EDITOR:
			return t('usermanager_text_artistseditor_d4c-web');
		case FeatureLabel.CHARTS_VIEWER:
			return t('usermanager_text_chartsviewer_d4c-web');
		case FeatureLabel.PLAYLISTS_REPORT:
			return t('usermanager_text_playlistsreportviewer_d4c-web');
		case FeatureLabel.USERS_EDITOR:
			return t('usermanager_text_usereditor_d4c-web');
		case FeatureLabel.DELIVERY_MONITORING_VIEWER:
			return t('usermanager_text_deliverymonitoringviewer_d4c-web');
		case FeatureLabel.PITCHING_TOOL:
			return t('usermanager_text_pitchingtooleditor_d4c-web');
		case FeatureLabel.CAMPAIGN_MANAGER:
			return t('analyticshome_title_campaignmgr_d4c-web');
		case FeatureLabel.COPYRIGHT_CLAIM:
			// fixme translate
			return 'Copyright claims';
		case FeatureLabel.BETA:
			break;
	}

	return feature;
};
